/**
 * Optimized Infinite Hero - Uses shared WebGL context and performance monitoring
 */

import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import WebGLContextManager from '@/utils/WebGLContextManager';
import PerformanceMonitor from '@/utils/PerformanceMonitor';
import { useViewportObserver } from '@/hooks/useViewportObserver';
import { gsap } from 'gsap';

interface OptimizedShaderPlaneProps {
  vertexShader: string;
  fragmentShader: string;
  uniforms: { [key: string]: { value: unknown } };
  pauseAnimations: boolean;
  qualityLevel: 'low' | 'medium' | 'high';
}

function OptimizedShaderPlane({
  vertexShader,
  fragmentShader,
  uniforms,
  pauseAnimations,
  qualityLevel,
}: OptimizedShaderPlaneProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { size, gl } = useThree();
  const lastTime = useRef(0);

  // Adjust shader complexity based on quality level
  const optimizedUniforms = useMemo(() => {
    const baseUniforms = {
      u_time: { value: 0 },
      u_resolution: { value: new THREE.Vector3(size.width, size.height, 1.0) },
      u_quality: { value: qualityLevel === 'high' ? 1.0 : qualityLevel === 'medium' ? 0.7 : 0.4 },
      ...uniforms,
    };
    return baseUniforms;
  }, [uniforms, size, qualityLevel]);

  useFrame((state, delta) => {
    if (!meshRef.current || pauseAnimations) return;

    const material = meshRef.current.material as THREE.ShaderMaterial;
    
    // Throttle updates based on quality level
    const updateInterval = qualityLevel === 'high' ? 0 : qualityLevel === 'medium' ? 33 : 66; // ms
    const now = performance.now();
    
    if (now - lastTime.current >= updateInterval) {
      material.uniforms.u_time.value = state.clock.elapsedTime * 0.5;
      material.uniforms.u_resolution.value.set(size.width, size.height, 1.0);
      lastTime.current = now;
    }
  });

  // Optimize shader based on quality level
  const optimizedFragmentShader = useMemo(() => {
    if (qualityLevel === 'low') {
      // Reduce complexity for low-end devices
      return fragmentShader.replace(
        '#define STEP 256',
        '#define STEP 128'
      ).replace(
        'f += 0.500000*(0.5+0.5*noise( p )); p = m*p*2.02;',
        'f += 0.500000*(0.5+0.5*noise( p )); p = m*p*2.02;'
      );
    }
    return fragmentShader;
  }, [fragmentShader, qualityLevel]);

  return (
    <mesh ref={meshRef}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={optimizedFragmentShader}
        uniforms={optimizedUniforms}
        side={THREE.DoubleSide}
        depthTest={false}
        depthWrite={false}
      />
    </mesh>
  );
}

interface OptimizedInfiniteHeroProps {
  isPreview?: boolean;
  id?: string;
}

export default function OptimizedInfiniteHero({ 
  isPreview = false, 
  id = 'default-infinite-hero' 
}: OptimizedInfiniteHeroProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextManager = useRef(WebGLContextManager.getInstance());
  const performanceMonitor = useRef(PerformanceMonitor.getInstance());
  
  // Viewport observation for performance optimization
  const { isVisible, pauseAnimations } = useViewportObserver({
    threshold: isPreview ? 0.1 : 0.05,
    rootMargin: isPreview ? '50px' : '200px',
  });

  // Performance state
  const [qualitySettings, setQualitySettings] = React.useState({
    qualityLevel: 'medium' as 'low' | 'medium' | 'high',
    shouldRender: true,
  });

  // Subscribe to performance updates
  useEffect(() => {
    const unsubscribe = performanceMonitor.current.subscribe((metrics, settings) => {
      setQualitySettings({
        qualityLevel: settings.shaderComplexity,
        shouldRender: metrics.fps > 15, // Stop rendering if FPS is too low
      });
    });

    return unsubscribe;
  }, []);

  // GSAP animation setup (only for non-preview mode)
  useEffect(() => {
    if (isPreview || !bgRef.current) return;

    gsap.set(bgRef.current, { filter: "blur(28px)" });

    const tl = gsap.timeline({ 
      defaults: { ease: "power2.out" },
      paused: pauseAnimations,
    });
    
    tl.to(bgRef.current, { filter: "blur(0px)", duration: 1.2 }, 0);

    return () => {
      tl.kill();
    };
  }, [isPreview, pauseAnimations]);

  // WebGL context management
  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.z = 1;

    // Register with context manager
    contextManager.current.registerScene(
      id,
      scene,
      camera,
      canvasRef.current,
      () => {
        // Animation callback - handled by React Three Fiber
      }
    );

    return () => {
      contextManager.current.unregisterScene(id);
    };
  }, [id]);

  // Shader configuration
  const vertexShader = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4(position, 1.0);
    }
  `;

  const fragmentShader = `
    precision highp float;

    varying vec2 vUv;
    uniform float u_time;
    uniform vec3 u_resolution;
    uniform float u_quality;

    #define STEP 256
    #define EPS .001

    float smin( float a, float b, float k )
    {
        float h = clamp( 0.5+0.5*(b-a)/k, 0.0, 1.0 );
        return mix( b, a, h ) - k*h*(1.0-h);
    }

    const mat2 m = mat2(.8,.6,-.6,.8);

    float noise( in vec2 x )
    {
      return sin(1.5*x.x)*sin(1.5*x.y);
    }

    float fbm6( vec2 p )
    {
        float f = 0.0;
        float quality = u_quality;
        f += 0.500000*(0.5+0.5*noise( p )) * quality; p = m*p*2.02;
        f += 0.250000*(0.5+0.5*noise( p )) * quality; p = m*p*2.03;
        f += 0.125000*(0.5+0.5*noise( p )) * quality; p = m*p*2.01;
        f += 0.062500*(0.5+0.5*noise( p )) * quality; p = m*p*2.04;
        if (quality > 0.7) {
          f += 0.015625*(0.5+0.5*noise( p ));
        }
        return f/0.96875;
    }

    mat2 getRot(float a)
    {
        float sa = sin(a), ca = cos(a);
        return mat2(ca,-sa,sa,ca);
    }

    vec3 _position;

    float sphere(vec3 center, float radius)
    {
        return distance(_position,center) - radius;
    }

    float swingPlane(float height)
    {
        vec3 pos = _position + vec3(0.,0.,u_time * 5.5);
        float def = fbm6(pos.xz * 0.25) * 0.5;
        
        float way = pow(abs(pos.x) * 34. ,2.5) *.0000125;
        def *= way;
        
        float ch = height + def;
        return max(pos.y - ch,0.);
    }

    float map(vec3 pos)
    {
        _position = pos;
        
        float dist;
        dist = swingPlane(0.);
        
        float sminFactor = 5.25;
        dist = smin(dist,sphere(vec3(0.,-15.,80.),60.),sminFactor);
        return dist;
    }

    vec3 getNormal(vec3 pos)
    {
        vec3 nor = vec3(0.);
        vec3 vv = vec3(0.,1.,-1.)*.01;
        nor.x = map(pos + vv.zxx) - map(pos + vv.yxx);
        nor.y = map(pos + vv.xzx) - map(pos + vv.xyx);
        nor.z = map(pos + vv.xxz) - map(pos + vv.xxy);
        nor /= 2.;
        return normalize(nor);
    }

    void mainImage( out vec4 fragColor, in vec2 fragCoord )
    {
      vec2 uv = (fragCoord.xy-.5*u_resolution.xy)/u_resolution.y;
        
        vec3 rayOrigin = vec3(uv + vec2(0.,6.), -1. );
        vec3 rayDir = normalize(vec3(uv , 1.));
        
        rayDir.zy = getRot(.15) * rayDir.zy;
        vec3 position = rayOrigin;
        
        float curDist;
        int nbStep = 0;
        int maxSteps = int(float(STEP) * u_quality);
        
        for(; nbStep < maxSteps; ++nbStep)
        {
            curDist = map(position);
            
            if(curDist < EPS)
                break;
            position += rayDir * curDist * 0.5;
        }
        
        float f;
        float dist = distance(rayOrigin,position);
        f = dist /(98.);
        f = float(nbStep) / float(maxSteps);
        
        f *= 0.9;
        vec3 col = vec3(f);
                
        fragColor = vec4(col,1.0);
    }
    
    void main() {
      vec4 fragColor;
      vec2 fragCoord = vUv * u_resolution.xy;
      mainImage(fragColor, fragCoord);
      gl_FragColor = fragColor;
    }
  `;

  // Don't render if performance is too poor or not visible
  if (!qualitySettings.shouldRender || (!isVisible && !isPreview)) {
    return (
      <div
        ref={rootRef}
        className="absolute inset-0 w-full h-full overflow-hidden bg-black"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="absolute inset-0 w-full h-full overflow-hidden bg-black"
    >
      <div className="absolute inset-0" ref={bgRef}>
        <Canvas
          ref={canvasRef}
          className="h-full w-full"
          dpr={Math.min(window.devicePixelRatio, qualitySettings.qualityLevel === 'high' ? 2 : 1)}
          performance={{ min: 0.5 }}
          frameloop={pauseAnimations ? 'never' : 'always'}
        >
          <OptimizedShaderPlane
            vertexShader={vertexShader}
            fragmentShader={fragmentShader}
            uniforms={{}}
            pauseAnimations={pauseAnimations}
            qualityLevel={qualitySettings.qualityLevel}
          />
        </Canvas>
      </div>

      {/* Remove radial gradient overlay in preview mode */}
      {!isPreview && (
        <div className="pointer-events-none absolute inset-0 [background:radial-gradient(120%_80%_at_50%_50%,_transparent_40%,_black_100%)]" />
      )}
    </div>
  );
}